import { useEffect, useRef, useState, type FormEvent } from "react";
import s from "./WorkOrders.module.css";
import { useCreateWorkOrder, useWorkOrders } from "../../api/queries";
import { useContracts } from "../contracts/ContractsRegister";
import { current } from "../../domain/contracts";
import {
  AREA_HINT,
  AREA_LABEL,
  AREAS,
  areaOf,
  contractorChoices,
  contractorOf,
  TRADE_HINT,
  TRADE_LABEL,
  TRADES,
  tradeOf,
  type Area,
  type Trade,
} from "../../domain/workOrderClass";
import { useReporter } from "../../assistant/report";
import {
  KIND_LABEL,
  PRIORITY_TITLE,
  WORK_ORDER_KINDS,
  WORK_ORDER_PRIORITIES,
  type WorkOrder,
  type WorkOrderKind,
  type WorkOrderPriority,
} from "../../domain/workOrders";
import { Button, Field, Input, Select } from "../../design/components";
import { t } from "../../i18n";

export interface WorkOrderPrefill {
  title?: string;
  kind?: WorkOrderKind;
  priority?: WorkOrderPriority;
  assetTag?: string;
  zoneCode?: string;
  description?: string;
  alertId?: string;
  alertTitle?: string;
  area?: Area;
  trade?: Trade;
  contractor?: string;
}

/**
 * Raise a work order — the platform's `WorkOrderIn`. It is created in
 * `draft`; approval is a separate, recorded step so the audit trail names
 * who decided the work was needed.
 */
export function CreateWorkOrderDialog({
  onClose,
  onCreated,
  prefill = {},
}: {
  onClose: () => void;
  onCreated: (wo: WorkOrder) => void;
  prefill?: WorkOrderPrefill;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const create = useCreateWorkOrder();
  const reporter = useReporter();
  const [kind, setKind] = useState<WorkOrderKind>(prefill.kind ?? "corrective");
  const [priority, setPriority] = useState<WorkOrderPriority>(prefill.priority ?? 3);
  const [assetTag, setAssetTag] = useState(prefill.assetTag ?? "");
  const [zoneCode, setZoneCode] = useState(prefill.zoneCode ?? "");
  const [title, setTitle] = useState(prefill.title ?? "");
  const [description, setDescription] = useState(prefill.description ?? "");
  const [slaHours, setSlaHours] = useState("");
  const [area, setArea] = useState<Area | "">(prefill.area ?? "");
  const [trade, setTrade] = useState<Trade | "">(prefill.trade ?? "");
  const [contractor, setContractor] = useState(prefill.contractor ?? "");
  /* the contractor was typed or picked by the person, so a trade change leaves it alone */
  const [contractorTouched, setContractorTouched] = useState(!!prefill.contractor);
  const orders = useWorkOrders({});
  const register = useContracts();
  const choices = contractorChoices(
    current(register.value).map((v) => v.data.provider),
    orders.data?.items ?? [],
  );

  /** Most work is in a contractor's scope: offer whoever did this trade in this area last. */
  const suggestFor = (nextArea: Area | "", nextTrade: Trade | "") => {
    if (contractorTouched || !nextTrade) return;
    const tally = new Map<string, number>();
    for (const w of orders.data?.items ?? []) {
      const c = contractorOf(w);
      if (!c || tradeOf(w).value !== nextTrade) continue;
      if (nextArea && areaOf(w).value !== nextArea) continue;
      tally.set(c, (tally.get(c) ?? 0) + 1);
    }
    const best = [...tally].sort((a, b) => b[1] - a[1])[0]?.[0];
    setContractor(best ?? "");
  };

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    // No close() on cleanup. StrictMode runs this cleanup between its two
    // mounts in development, and the close event it fires unmounted the
    // dialog as it opened — the button did nothing. An unmounted dialog
    // leaves the top layer by itself.
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    create.mutate(
      {
        title: title.trim(),
        kind,
        priority,
        assetTag: assetTag.trim() || undefined,
        zoneCode: zoneCode.trim() || undefined,
        description: description.trim() || undefined,
        slaHours: slaHours ? Number(slaHours) : undefined,
        alertId: prefill.alertId,
        area: area || undefined,
        trade: trade || undefined,
        contractor: contractor.trim() || undefined,
      },
      {
        onSuccess: (raised) => {
          reporter.workOrderRaised(raised);
          onCreated(raised);
        },
      },
    );
  };

  return (
    <dialog ref={ref} onClose={onClose} style={dialogStyle}>
      <form onSubmit={submit} className={s.form}>
        <h2 className={s.formWide}>{t("New Work Order")}</h2>
        {prefill.alertId && (
          <div className={`${s.formWide} muted`} style={{ fontSize: 13.5 }}>
            {t("From alert:")} <b>{prefill.alertTitle ?? prefill.alertId}</b>
          </div>
        )}
        <div className={s.formWide}>
          <Field label="Title">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
          </Field>
        </div>
        <Field label="Area">
          <Select
            value={area}
            onChange={(e) => {
              const v = e.target.value as Area | "";
              setArea(v);
              suggestFor(v, trade);
            }}
          >
            <option value="">{t("Choose area")}</option>
            {AREAS.map((a) => (
              <option key={a} value={a}>
                {t(AREA_LABEL[a])} — {t(AREA_HINT[a])}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Trade">
          <Select
            value={trade}
            onChange={(e) => {
              const v = e.target.value as Trade | "";
              setTrade(v);
              suggestFor(area, v);
            }}
          >
            <option value="">{t("Choose trade")}</option>
            {TRADES.map((k) => (
              <option key={k} value={k}>
                {t(TRADE_LABEL[k])} — {t(TRADE_HINT[k])}
              </option>
            ))}
          </Select>
        </Field>
        <div className={s.formWide}>
          <Field label="Contractor">
            <Input
              value={contractor}
              list="wo-contractors"
              onChange={(e) => {
                setContractor(e.target.value);
                setContractorTouched(true);
              }}
              placeholder={t("Leave blank if your own team does it")}
            />
            <datalist id="wo-contractors">
              {choices.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Field>
        </div>
        <Field label="Kind">
          <Select value={kind} onChange={(e) => setKind(e.target.value as WorkOrderKind)}>
            {WORK_ORDER_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(KIND_LABEL[k])}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Priority">
          <Select value={priority} onChange={(e) => setPriority(Number(e.target.value) as WorkOrderPriority)}>
            {WORK_ORDER_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {t(PRIORITY_TITLE[p])}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="SLA hours (optional)">
          <Input
            type="number"
            min={1}
            max={8760}
            step={1}
            inputMode="numeric"
            value={slaHours}
            onChange={(e) => setSlaHours(e.target.value)}
            placeholder={t("e.g. 4, 24, 72")}
            title={t("How many hours the crew has to complete this order.")}
          />
        </Field>
        <div className="muted" style={{ fontSize: 12, alignSelf: "end", paddingBottom: 6 }}>
          {t("Hours from when the order is created. Leave blank for no deadline.")}
        </div>
        <Field label="Asset tag (optional)">
          <Input
            value={assetTag}
            onChange={(e) => setAssetTag(e.target.value)}
            placeholder={t("e.g. {example}", { example: "S2403" })}
          />
        </Field>
        <Field label="Zone code (optional)">
          <Input
            value={zoneCode}
            onChange={(e) => setZoneCode(e.target.value)}
            placeholder={t("e.g. {example}", { example: "TWY-B" })}
          />
        </Field>
        <div className={s.formWide}>
          <Field label="Description">
            <textarea
              className={s.note}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              style={textareaStyle}
            />
          </Field>
        </div>
        <div className={s.formFoot}>
          {create.isError && (
            <span style={{ color: "var(--red)", marginInlineEnd: "auto" }} role="alert">
              {(create.error as Error).message}
            </span>
          )}
          <Button onClick={onClose} disabled={create.isPending}>
            {t("Cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? t("Creating…") : t("Create as draft")}
          </Button>
        </div>
      </form>
    </dialog>
  );
}

const dialogStyle: React.CSSProperties = {
  background: "var(--panel)",
  color: "var(--ink)",
  border: "1px solid var(--line)",
  borderRadius: "var(--radius)",
  padding: 20,
  width: "min(560px, 92vw)",
};
const textareaStyle: React.CSSProperties = {
  padding: "6px 10px",
  border: "1px solid var(--line)",
  borderRadius: "var(--radius-sm)",
  background: "var(--panel2)",
  color: "var(--ink)",
  font: "inherit",
  fontSize: 13.5,
};
